import React, { FC, useEffect, useState } from 'react';
import { Col, Layout, Row } from 'antd';
import moment from 'moment';
import { ESidebarMenuKey, ESidebarMenuLabel } from 'common/models/commonModel';
import { PageHeader } from 'common/components/Page/PageHeader';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { PageContent } from 'common/components/Page/PageContent';
import { PeriodFilterToolbar } from 'common/components/PeriodFilterToolbar';
import { getFilterWeek } from 'common/helpers/date.helper';
import { usePeriodFilter } from 'common/hooks/usePeriodFilter';
import { PeriodFilterCard } from 'common/components/PeriodFilterCard';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getAnalyzeChannelsStoreCollection } from 'app/store/reducers/analyzeChannels.reducer';
import { AnalyzeChannelsChannelShareBlock } from 'entities/AnalyzeChannels/components/AnalyzeChannelsChannelShareBlock';
import { AnalyzeChannelsChannelShareWeeklyBlock } from 'entities/AnalyzeChannels/components/AnalyzeChannelsChannelShareWeeklyBlock';
import { AnalyzeChannelsChannelShareShiftsBlock } from 'entities/AnalyzeChannels/components/AnalyzeChannelsChannelShareShiftsBlock';

export const AnalyzeChannelsPage: FC = () => {
  const dispatch = useAppDispatch();

  const { analyzeChannelsStoreCollection } = useAppSelector((state) => state.analyzeChannels);

  const [storeCode, setStoreCode] = useState<string | number | undefined>(undefined);

  const periodFilter = usePeriodFilter();

  useEffect(() => {
    dispatch(getAnalyzeChannelsStoreCollection());
  }, []);

  const resolvedWeek = periodFilter.week ?? moment().isoWeek();
  const resolvedYear = periodFilter.year ?? moment().isoWeekYear();
  const weekStart = moment().isoWeekYear(resolvedYear).isoWeek(resolvedWeek).startOf('isoWeek');
  const weekEnd = weekStart.clone().endOf('isoWeek');
  const coverage = `${weekStart.format('MMM D, YYYY')} to ${weekEnd.format('MMM D, YYYY')} (12:00 am — 11:59 pm)`;
  // Default = current week, sent explicitly so the BE window always matches the coverage label.
  const filterWeek = getFilterWeek(resolvedYear, resolvedWeek);

  const changeStore = (value?: string | number) => {
    setStoreCode(value);
  };

  const headerFilters = (
    <Row gutter={[8, 8]} align="middle">
      <Col style={{ maxWidth: 149 }}>
        <PageStoreSelector stores={analyzeChannelsStoreCollection?.data} onChange={changeStore} allowAll />
      </Col>
      <Col>
        <PeriodFilterToolbar
          year={periodFilter.year}
          week={periodFilter.week}
          filterActive={periodFilter.open || !!storeCode}
          onWeekSelect={periodFilter.onWeekSelect}
          onOpen={periodFilter.openFilterCard}
        />
      </Col>
    </Row>
  );

  return (
    <Layout className="relative height-full flex flex__column">
      <PageHeader
        title={
          <div>
            <span>{ESidebarMenuLabel[ESidebarMenuKey.AnalyzeChannels]}</span>
          </div>
        }
        extra={headerFilters}
      />

      <PageContent>
        <div className="page-content__container">
          <div className="page-content__scroll-container scrollbar-y">
            <div style={{ overflow: 'hidden' }}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <AnalyzeChannelsChannelShareBlock
                    dateRange={coverage}
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
                <Col span={24}>
                  <AnalyzeChannelsChannelShareWeeklyBlock
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
                <Col span={24}>
                  <AnalyzeChannelsChannelShareShiftsBlock
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </div>

        <PeriodFilterCard
          open={periodFilter.open}
          year={periodFilter.year}
          week={periodFilter.week}
          onYearChange={periodFilter.changeYear}
          onWeekChange={periodFilter.changeWeek}
          onCancel={periodFilter.closeFilterCard}
        />
      </PageContent>
    </Layout>
  );
};
